import { useCallback } from "react";
import { CriteriaTreeBuilderValue } from "./criteria-tree-builder-types";
import {
  addChildToGroup,
  createEmptyGroup,
  createEmptyStatement,
  removeNodeById,
  updateNodeById,
} from "./criteria-tree-builder-utils";

/**
 * State mutations for the criteria tree builder. The builder is fully controlled:
 * pass the current tree via `value` and receive the updated tree in `onChange`.
 */
export const useCriteriaTreeBuilder = (
  value: CriteriaTreeBuilderValue,
  onChange: (value: CriteriaTreeBuilderValue) => void,
) => {
  const updateStatement = useCallback(
    (statementId: string, statementValue: string) => {
      onChange(
        updateNodeById(value, statementId, (node) => ({
          ...node,
          value: statementValue,
        })),
      );
    },
    [value, onChange],
  );

  const toggleGroupConjunction = useCallback(
    (groupId: string, conjunction: "AND" | "OR") => {
      onChange(
        updateNodeById(value, groupId, (node) => ({
          ...node,
          conjunction,
        })),
      );
    },
    [value, onChange],
  );

  const addCondition = useCallback(
    (groupId: string) => {
      onChange(addChildToGroup(value, groupId, createEmptyStatement()));
    },
    [value, onChange],
  );

  const addNestedGroup = useCallback(
    (groupId: string) => {
      onChange(addChildToGroup(value, groupId, createEmptyGroup()));
    },
    [value, onChange],
  );

  const removeNode = useCallback(
    (nodeId: string) => {
      onChange(removeNodeById(value, nodeId));
    },
    [value, onChange],
  );

  return {
    updateStatement,
    toggleGroupConjunction,
    addCondition,
    addNestedGroup,
    removeNode,
  } as const;
};